import React, { useEffect, useState } from 'react';
import MetricsTable from '@/pages/intelligence/pages/TopCharts/PCGames/MetricsTable';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { mauList } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import moment from 'moment';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';
import { getDeviceValuesByPageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import {
  commonPCConsoleDefaultFilterValue,
  handleMetricTableFilterParamsToApi,
} from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import { defaultMetrictableApiParams } from '@intelligence/pages/TopCharts/PCGames/MetricsTable/utils/constant';
import { useIntl, history } from 'umi';
import { getTopPCMetricTableCustomValidUrlParams, topchartPCMAUValidCfg, hasShortLinkRatio } from '@intelligence/_config/urlPageValidCfg';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_CHART_METRIC, URL_CHART_TYPE } from '@intelligence/utils/urlPathCfg';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

export default function MAU() {
  const { formatMessage } = useIntl(); // 中英切换方法
  const pageId = TopchartPageId.MAU;
  const entityType = 'pc';

  const { filterLoading, regionOptions } = useMarketFilterApi({});

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.pcMAU?.type,
  });

  const defaultFilterValue = {
    ...commonPCConsoleDefaultFilterValue,
    [filterKeyMap.MAU_DEVICE]: { value: getDeviceValuesByPageId(pageId) },
  };
  
  const hasUrlShortLink = hasShortLinkRatio.topPCMAU;
  const [shortLinkLoading, setShortLinkLoading] = useState(hasUrlShortLink);
  const [urlCustomParams, setUrlCustomParams] = useState({});
  const [shortLinkValidQuery, setShortLinkValidQuery] = useState({
    [URL_CHART_TYPE]: topchartPCMAUValidCfg?.[URL_CHART_TYPE]?.[0],
    [URL_CHART_METRIC]: topchartPCMAUValidCfg?.[URL_CHART_METRIC]?.[0],
  });
  const [shortLinkFilterValue, setShortLinkFilterValue] = useState(null);

  const initPage = async () => {
    setShortLinkLoading(true);
    // 在url上取token，有则请求接口， 判断异常，返回函数值和url上的其他配置参数
    const { [URL_FILTER_KEY]: urlFilter } = await getOriginalShortLinkFilter();
    if (urlFilter) {
      // 有的筛选器的token时才用后面的参数，没有则不用
      const { params, validQuery } = getTopPCMetricTableCustomValidUrlParams(history, topchartPCMAUValidCfg, mauList);
      setUrlCustomParams({ ...params });
      setShortLinkValidQuery({ ...shortLinkValidQuery, ...validQuery });
      setShortLinkFilterValue(urlFilter);
    }
    setShortLinkLoading(false);
  };

  useEffect(() => {
    buriedPointPage(dbiPageLogMes.topChartsPCGamesMAU);
    // 进入页面时判断是否有tokenQuery等，有则进入
    initPage();
  }, []);

  const filterCfg: TopchartFilterCfgType = {
    filterKeyList: [
      filterKeyMap.MY_FILTER,
      filterKeyMap.RANGE_TIME,
      filterKeyMap.REGION,
      filterKeyMap.GENRETOR,
      filterKeyMap.ALL_Games,
      filterKeyMap.MAU_DEVICE,
      filterKeyMap.ALL_COMPANYS,
      filterKeyMap.RELEASE_TIME,
    ],
    companyFilterParams: {
      entity_type: entityType,
      region_type: 'market',
      regions: ['global'],
      granularity: 'monthly',
      start_date: '',
      end_date: '',
      search_name: '',
      page: 1,
      page_size: 500,
      source: 'newzoo',
      metric: 'mau',
      data_sources: ['newzoo', 'npd', 'steamcharts'],
      source_metric: {
        newzoo: ['mau'],
      },
    },
    defaultValue: defaultFilterValue,
    resetFilterValue: {
      ...defaultFilterValue,
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: [moment().subtract(6, 'months'), moment().subtract(1, 'months')],
      },
    },
    specialParams: {
      regionOptions, // 国家筛选器options,
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      rangeTimeType: FilterTimeType.rangeMonth,
      disableIegg: false,
      disableAnnie: true,
      [filterKeyMap.ALL_Games]: {
        params: { entity_type: entityType, game_type: 'current_game' },
      },
      entityType
    },
    iconText: (
      <div>
        <p>{formatMessage({ id: 'dbi_releasedatefilter_info' })}</p>
      </div>
    ),
    showConfirmButton: true,
    haveFilterSaveBtn: true,
    handleFilterToApiParams: (params: any) => handleMetricTableFilterParamsToApi(params, entityType),
    hasUrlShortLink,
  };

  const defaultParams = {
    ...defaultMetrictableApiParams,
    entity_type: entityType,
    order_metric: 'mau',
    order_source: 'newzoo',
    source_metrics: {
      newzoo: ['mau'],
      newzoo_weight: ['acquisition'],
    },
    topchart_name: 'MAU',
    region_type: 'market',
    regions: ['global'],
    date_type: 'monthly',
  };

  return (
    <>
      <MarqueeBanner />
      <MetricsTable
      entityType={entityType}
      pageId={pageId}
      pageLogInfo={dbiPageLogMes.topChartsPCGamesMAU}
      tableCloumsList={mauList}
      hasRegionFilter={true}
      cardMetricName={formatMessage({ id: 'dbi_mau' })}
      filterCfg={filterCfg}
      filterLoading={filterLoading || shortLinkLoading}
      rangeTimeComponentDefault={latestTimeTypeHasData}
      defaultParams={defaultParams}
      urlCustomParams={urlCustomParams}
      shortLinkValidQuery={shortLinkValidQuery}
      shortLinkFilterValue={shortLinkFilterValue}
    />
    </>
  );
}
